import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Rnd } from 'react-rnd';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GestureIcon from '@mui/icons-material/Gesture';
import ImageIcon from '@mui/icons-material/Image';
import { FormEditorDto, FormFieldEditorDto, FormFieldType, PdfFieldConfig, LabelConfig, SectionPdfConfig } from '../../types/formEditor';
import { AutoLayoutSettings } from './AutoLayoutSettingsDialog';

interface FormCanvasProps {
  form?: FormEditorDto;
  fields: FormFieldEditorDto[];
  selectedFieldId?: number;
  selectedSectionId?: number;
  onFieldSelect: (fieldId: number) => void;
  onSectionSelect: (sectionId: number) => void;
  onFieldUpdate: (fieldId: number, pdfConfig: PdfFieldConfig) => void;
  onSectionUpdate: (sectionId: number, pdfConfig: SectionPdfConfig) => void;
  scale: number;
  currentPage: number;
  headerConfig?: string;
  autoLayoutSettings: AutoLayoutSettings;
}

const A4_WIDTH = 595;
const A4_HEIGHT = 842;

// Header configuration interfaces
interface HeaderElement {
  type: string;
  content: string;
  fontSize: number;
  fontWeight: string;
  color: string;
  fontFamily: string;
  x: number | string;
  y: number;
  alignment: string;
}

interface HeaderConfig {
  type: string;
  height: number;
  backgroundColor: string;
  rightPadding: number;
  elements: HeaderElement[];
}

// Render header on canvas
const renderHeader = (headerConfigJson: string, scale: number) => {
  try {
    const headerConfig: HeaderConfig = JSON.parse(headerConfigJson);

    return (
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: A4_WIDTH, // Match canvas content width
          height: headerConfig.height,
          backgroundColor: headerConfig.backgroundColor,
          zIndex: 1,
          pointerEvents: 'none', // Don't interfere with field interactions
          transform: `scale(${scale})`,
          transformOrigin: 'top left'
        }}
      >
        {headerConfig.elements.map((element, index) => {
          if (element.type === 'text') {
            // Calculate position - handle "right" alignment
            let xPos = element.x;
            if (element.x === 'right') {
              xPos = A4_WIDTH - headerConfig.rightPadding - 200; // Approximate text width
            }

            return (
              <div
                key={index}
                style={{
                  position: 'absolute',
                  left: typeof xPos === 'number' ? xPos : 0,
                  top: element.y,
                  fontSize: element.fontSize * scale,
                  fontWeight: element.fontWeight,
                  color: element.color,
                  fontFamily: element.fontFamily,
                  whiteSpace: 'nowrap'
                }}
              >
                {element.content}
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  } catch (error) {
    console.error('Failed to render header:', error);
    return null;
  }
};

const getFieldIcon = (fieldType: FormFieldType | string) => {
  // Convert string enum to numeric if needed
  const numericFieldType = typeof fieldType === 'string'
    ? FormFieldType[fieldType as keyof typeof FormFieldType]
    : fieldType;

  switch (numericFieldType) {
    case FormFieldType.Bool:
      return <CheckBoxIcon fontSize="small" />;
    case FormFieldType.Dropdown:
      return <ArrowDropDownCircleIcon fontSize="small" />;
    case FormFieldType.CheckboxList:
      return <CheckBoxIcon fontSize="small" />;
    case FormFieldType.Date:
    case FormFieldType.DateTime:
      return <CalendarTodayIcon fontSize="small" />;
    case FormFieldType.HorizontalRule:
      return <ImageIcon fontSize="small" />;
    default:
      return <TextFieldsIcon fontSize="small" />;
  }
};

const getDefaultPdfConfig = (pageNumber: number = 1): PdfFieldConfig => ({
  x: 50,
  y: 50,
  width: 150,
  height: 25,
  fontFamily: 'Arial',
  fontSize: 12,
  fontColor: '#000000',
  tabOrder: 0,
  alignment: 'Left',
  isMultiline: false,
  isBold: false,
  isItalic: false,
  pageWidth: A4_WIDTH,
  pageHeight: A4_HEIGHT,
  pageNumber
});

const getDefaultLabelConfig = (fieldX: number, fieldY: number, fieldHeight: number): LabelConfig => ({
  x: fieldX,
  y: fieldY, // Same Y-axis as field
  width: 150,
  height: fieldHeight,
  fontFamily: 'Arial',
  fontSize: 10,
  fontColor: '#000000',
  isBold: false,
  isItalic: false,
  alignment: 'Left',
  verticalAlignment: 'Top',
  isVisible: true
});

const getDefaultSectionPdfConfig = (pageNumber: number = 1): SectionPdfConfig => ({
  x: 50,
  y: 100,
  width: 495,
  height: 200,
  borderColor: '#cccccc',
  borderWidth: 1,
  showBorder: true,
  pageNumber
});

export const FormCanvas: React.FC<FormCanvasProps> = ({
  form,
  fields,
  selectedFieldId,
  selectedSectionId,
  onFieldSelect,
  onSectionSelect,
  onFieldUpdate,
  onSectionUpdate,
  scale,
  currentPage,
  headerConfig,
  autoLayoutSettings
}) => {

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        backgroundColor: '#e0e0e0',
        padding: 2,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start'
      }}
    >
      <Box
        sx={{
          width: A4_WIDTH * scale,
          height: A4_HEIGHT * scale,
          backgroundColor: 'white',
          position: 'relative',
          boxShadow: '0 0 10px rgba(0,0,0,0.2)',
          border: '1px solid #ccc'
        }}
      >
        {/* Grid overlay */}
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: A4_WIDTH * scale,
            height: A4_HEIGHT * scale,
            pointerEvents: 'none'
          }}
        >
          <defs>
            <pattern
              id="grid"
              width={10 * scale}
              height={10 * scale}
              patternUnits="userSpaceOnUse"
            >
              <path
                d={`M ${10 * scale} 0 L 0 0 0 ${10 * scale}`}
                fill="none"
                stroke="#ddd"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        {/* Render header if configured */}
        {headerConfig && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: A4_WIDTH * scale,
              height: (() => {
                try {
                  const config = JSON.parse(headerConfig);
                  return (config.height || 0) * scale;
                } catch {
                  return 0;
                }
              })(),
              backgroundColor: (() => {
                try {
                  const config = JSON.parse(headerConfig);
                  return config.backgroundColor || 'transparent';
                } catch {
                  return 'transparent';
                }
              })(),
              zIndex: 1,
              pointerEvents: 'none'
            }}
          >
            {(() => {
              try {
                const headerConfigParsed = JSON.parse(headerConfig);
                return headerConfigParsed.elements?.map((element: any, index: number) => {
                  if (element.type === 'text') {
                    let xPos = element.x;
                    if (element.x === 'right') {
                      xPos = A4_WIDTH - (headerConfigParsed.rightPadding || 0) - 200;
                    }
                    return (
                      <div
                        key={index}
                        style={{
                          position: 'absolute',
                          left: typeof xPos === 'number' ? xPos * scale : 0,
                          top: element.y * scale,
                          fontSize: element.fontSize * scale,
                          fontWeight: element.fontWeight,
                          color: element.color,
                          fontFamily: element.fontFamily,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {element.content}
                      </div>
                    );
                  }
                  return null;
                });
              } catch {
                return null;
              }
            })()}
          </div>
        )}

        {/* Render sections with borders and fields */}
        {form?.formSections.map((section) => {
          // Find fields for this section on current page
          const sectionFields = fields.filter(f => f.formSectionId === section.id);

          // Get section PDF config
          const sectionPdfConfig = section.pdfConfig || getDefaultSectionPdfConfig(currentPage);
          const isSectionSelected = section.id === selectedSectionId;

          // Only render section if it's configured for the current page
          // AND either has fields on this page OR border is configured to show
          const isSectionOnCurrentPage = sectionPdfConfig.pageNumber === currentPage;
          const shouldRenderSection = isSectionOnCurrentPage && (sectionFields.length > 0 || sectionPdfConfig.showBorder);

          if (!shouldRenderSection) return null;

          return (
            <React.Fragment key={`section-${section.id}`}>
              {/* Section Border - only show if on current page */}
              {sectionPdfConfig.showBorder && sectionPdfConfig.pageNumber === currentPage && (
                <Rnd
                  key={`section-border-${section.id}-${isSectionSelected}-${scale}`}
                  default={{
                    x: sectionPdfConfig.x * scale,
                    y: sectionPdfConfig.y * scale,
                    width: sectionPdfConfig.width * scale,
                    height: sectionPdfConfig.height * scale
                  }}
                  position={undefined}
                  scale={scale}
                  onDragStop={(e, d) => {
                    if (!isSectionSelected) return;
                    onSectionUpdate(section.id, {
                      ...sectionPdfConfig,
                      x: Math.round(d.x / scale),
                      y: Math.round(d.y / scale)
                    });
                  }}
                  onResizeStop={(e, direction, ref, delta, position) => {
                    if (!isSectionSelected) return;
                    onSectionUpdate(section.id, {
                      ...sectionPdfConfig,
                      width: Math.round(parseInt(ref.style.width) / scale),
                      height: Math.round(parseInt(ref.style.height) / scale),
                      x: Math.round(position.x / scale),
                      y: Math.round(position.y / scale)
                    });
                  }}
                  bounds="parent"
                  disableDragging={!isSectionSelected}
                  enableResizing={isSectionSelected ? {
                    top: true,
                    right: true,
                    bottom: true,
                    left: true,
                    topRight: true,
                    bottomRight: true,
                    bottomLeft: true,
                    topLeft: true
                  } : false}
                  style={{
                    border: isSectionSelected
                      ? `2px solid #9c27b0`
                      : `${sectionPdfConfig.borderWidth}px solid ${sectionPdfConfig.borderColor}`,
                    backgroundColor: isSectionSelected ? 'rgba(156, 39, 176, 0.05)' : 'transparent',
                    cursor: isSectionSelected ? 'move' : 'default',
                    zIndex: isSectionSelected ? 500 : 0,
                    pointerEvents: isSectionSelected ? 'auto' : 'none'
                  }}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onSectionSelect(section.id);
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -20 * scale,
                      left: 0,
                      fontSize: `${11 * scale}px`,
                      fontWeight: 'bold',
                      color: isSectionSelected ? '#9c27b0' : '#666',
                      backgroundColor: 'white',
                      padding: `${2 * scale}px ${6 * scale}px`,
                      borderRadius: `${3 * scale}px`,
                      pointerEvents: 'none'
                    }}
                  >
                    {section.description}
                  </Box>
                </Rnd>
              )}
              {/* Section Fields and Labels */}
              {sectionFields.map((field) => {
                const pdfConfig = field.pdfConfig || getDefaultPdfConfig(currentPage);
                const labelConfig = pdfConfig.labelConfig || getDefaultLabelConfig(pdfConfig.x, pdfConfig.y, pdfConfig.height);
                const isSelected = field.id === selectedFieldId;

                return (
                  <React.Fragment key={`field-${field.id}`}>
                    {/* Label */}
                    {labelConfig.isVisible && (
                      <Rnd
                        key={`label-${field.id}-${isSelected}-${scale}`}
                        default={{
                          x: labelConfig.x * scale,
                          y: labelConfig.y * scale,
                          width: labelConfig.width * scale,
                          height: labelConfig.height * scale
                        }}
                        position={undefined}
                        scale={scale}
                        onDragStop={(e, d) => {
                          if (!isSelected) return;
                          onFieldUpdate(field.id, {
                            ...pdfConfig,
                            labelConfig: {
                              ...labelConfig,
                              x: Math.round(d.x / scale),
                              y: Math.round(d.y / scale)
                            }
                          });
                        }}
                        onResizeStop={(e, direction, ref, delta, position) => {
                          if (!isSelected) return;
                          onFieldUpdate(field.id, {
                            ...pdfConfig,
                            labelConfig: {
                              ...labelConfig,
                              width: Math.round(parseInt(ref.style.width) / scale),
                              height: Math.round(parseInt(ref.style.height) / scale),
                              x: Math.round(position.x / scale),
                              y: Math.round(position.y / scale)
                            }
                          });
                        }}
                        bounds="parent"
                        disableDragging={!isSelected}
                        enableResizing={isSelected ? {
                          top: true,
                          right: true,
                          bottom: true,
                          left: true,
                          topRight: true,
                          bottomRight: true,
                          bottomLeft: true,
                          topLeft: true
                        } : false}
                        style={{
                          border: isSelected ? '1px dashed #ff9800' : '1px dashed transparent',
                          backgroundColor: isSelected ? 'rgba(255, 152, 0, 0.05)' : 'transparent',
                          cursor: isSelected ? 'move' : 'default',
                          zIndex: isSelected ? 999 : 0,
                          pointerEvents: isSelected ? 'auto' : 'none'
                        }}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          onFieldSelect(field.id);
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: labelConfig.verticalAlignment === 'Bottom' ? 'flex-end' :
                              labelConfig.verticalAlignment === 'Middle' ? 'center' : 'flex-start',
                            height: '100%',
                            fontSize: `${labelConfig.fontSize * scale}px`,
                            fontFamily: labelConfig.fontFamily,
                            fontWeight: labelConfig.isBold ? 'bold' : 'normal',
                            fontStyle: labelConfig.isItalic ? 'italic' : 'normal',
                            color: labelConfig.fontColor,
                            textAlign: labelConfig.alignment.toLowerCase() as any,
                            overflow: 'hidden',
                            padding: `${autoLayoutSettings.labelPadding * scale}px`
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              flex: 1,
                              fontSize: 'inherit'
                            }}
                          >
                            {field.description || field.code}
                          </Typography>
                        </Box>
                      </Rnd>
                    )}

                    {/* Field */}
                    <Rnd
                      key={`field-${field.id}-${isSelected}-${scale}`}
                      default={{
                        x: pdfConfig.x * scale,
                        y: pdfConfig.y * scale,
                        width: pdfConfig.width * scale,
                        height: pdfConfig.height * scale
                      }}
                      position={undefined}
                      scale={scale}
                      onDragStop={(e, d) => {
                        if (!isSelected) return;
                        onFieldUpdate(field.id, {
                          ...pdfConfig,
                          x: Math.round(d.x / scale),
                          y: Math.round(d.y / scale)
                        });
                      }}
                      onResizeStop={(e, direction, ref, delta, position) => {
                        if (!isSelected) return;
                        onFieldUpdate(field.id, {
                          ...pdfConfig,
                          width: Math.round(parseInt(ref.style.width) / scale),
                          height: Math.round(parseInt(ref.style.height) / scale),
                          x: Math.round(position.x / scale),
                          y: Math.round(position.y / scale)
                        });
                      }}
                      bounds="parent"
                      disableDragging={!isSelected}
                      enableResizing={isSelected ? {
                        top: true,
                        right: true,
                        bottom: true,
                        left: true,
                        topRight: true,
                        bottomRight: true,
                        bottomLeft: true,
                        topLeft: true
                      } : false}
                      style={{
                        border: isSelected ? '2px solid #1976d2' : '1px solid #666',
                        backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.1)' : 'rgba(255, 255, 255, 0.8)',
                        cursor: 'move',
                        zIndex: isSelected ? 1000 : 1
                      }}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        onFieldSelect(field.id);
                      }}
                    >
                      <Box
                        className="drag-handle"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5 * scale,
                          padding: `${2 * scale}px ${4 * scale}px`,
                          height: '100%',
                          fontSize: `${pdfConfig.fontSize * scale}px`,
                          fontFamily: pdfConfig.fontFamily,
                          fontWeight: pdfConfig.isBold ? 'bold' : 'normal',
                          fontStyle: pdfConfig.isItalic ? 'italic' : 'normal',
                          color: pdfConfig.fontColor,
                          textAlign: pdfConfig.alignment.toLowerCase() as any,
                          overflow: 'hidden'
                        }}
                      >
                        {getFieldIcon(field.fieldType)}
                        {(() => {
                          const normalized = typeof field.fieldType === 'string'
                            ? FormFieldType[field.fieldType as keyof typeof FormFieldType]
                            : field.fieldType;

                          if (normalized === FormFieldType.Dimension) {
                            return (
                              <Box sx={{ display: 'flex', gap: 0.5 * scale, flex: 1, height: '100%', alignItems: 'center' }}>
                                {['H', 'W', 'D'].map(dim => (
                                  <Box key={dim} sx={{
                                    flex: 1,
                                    height: '80%',
                                    border: '1px solid #ccc',
                                    backgroundColor: 'white',
                                    fontSize: `${8 * scale}px`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '2px',
                                    position: 'relative'
                                  }}>
                                    <span style={{ fontWeight: 'bold', color: '#666' }}>{dim}</span>
                                  </Box>
                                ))}
                              </Box>
                            );
                          }

                          return (
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                flex: 1,
                                fontSize: 'inherit',
                                fontWeight: 'medium'
                              }}
                            >
                              {field.description || field.code}
                            </Typography>
                          );
                        })()}
                        {pdfConfig.tabOrder > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: `${10 * scale}px`,
                              backgroundColor: '#1976d2',
                              color: 'white',
                              borderRadius: '50%',
                              width: `${16 * scale}px`,
                              height: `${16 * scale}px`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            {pdfConfig.tabOrder}
                          </Typography>
                        )}
                      </Box>
                    </Rnd>
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          );
        })}

        {/* Page Indicator */}
        <Box
          sx={{
            position: 'absolute',
            top: 10 * scale,
            right: 10 * scale,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: `${4 * scale}px ${8 * scale}px`,
            borderRadius: `${4 * scale}px`,
            fontSize: `${12 * scale}px`,
            fontWeight: 'bold',
            pointerEvents: 'none',
            zIndex: 100
          }}
        >
          Page {currentPage}
        </Box>

        {/* Instructions */}
        {fields.length === 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              color: '#999',
              pointerEvents: 'none'
            }}
          >
            <Typography variant="h6">Click anywhere to add a field</Typography>
            <Typography variant="body2">Page {currentPage}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
